import { api, getData } from 'services/api';
import { ResponsesData } from 'features/CustomFieldsResponses/types';

import { EventGate, PartnerSettings, QrPayload, ScanValidationResponse } from './types';

/**
 * Validate + mark-scanned an encrypted QR ticket payload. Zone-neutral clone of the dark Scanner
 * service: POST /tickets/qr_data/validate. `time_slot_id` is hardcoded 0 — the scan path carries no
 * gate/slot dimension from the client (the server attributes the scan to the authenticated scanner's
 * assigned gate; see docs/iframe-communication.md + Scanner/CLAUDE.md invariant 4).
 */
export const validateEncryptedTicket = ({ qrData, eventID }: { qrData: QrPayload; eventID: number }) =>
	api.post<ScanValidationResponse>('/tickets/qr_data/validate', {
		qr_data: qrData,
		event_id: eventID,
		time_slot_id: 0,
	});

/**
 * Manual approval: validate + mark-scanned by ticket id, with no QR (POST /tickets/validate). Used by the
 * Manual-approval method after the operator finds a guest and taps "Check in". `ticket_ids` and `event_id` are
 * sent as STRINGS — the backend's `Validation` struct types them `[]string` / `string`, so numeric JSON fails
 * gin's bind with HTTP 400 (matches the working BoxOffice + dark-Scanner callers). `time_slot_id` is a `uint`
 * (numeric 0 is correct) and carries no gate/slot dimension from the client (server attributes the gate).
 */
export const validateTicketsManually = ({ ticketIds, eventID }: { ticketIds: number[]; eventID: number }) =>
	api.post<ScanValidationResponse>('/tickets/validate', {
		ticket_ids: ticketIds.map(String),
		event_id: String(eventID),
		time_slot_id: 0,
	});

/**
 * Undo a check-in (POST /tickets/:ticket_id/undo-check-in) for the show the scanner is on. `today` asks the server
 * to resolve today's show in a festival group first, as a scan does; for a multi-day pass it clears only that day's
 * entry, so the pass scans again that day. Admin/editor only.
 */
export const undoCheckIn = ({ ticketId, eventID }: { ticketId: number; eventID: number }) =>
	api.post<{ ticket_id: number; event_id: number; pass_day: boolean }>(`/tickets/${ticketId}/undo-check-in`, {
		event_id: eventID,
		today: true,
	});

/** First-page cap for the door attendee list. Large enough to populate the manual look-up comboboxes for a
 * typical event in one request; events beyond this would need server-side search / pagination. Exported so the
 * panel can detect a truncated list and tell the operator (see ManualApprovalPanel). */
export const ATTENDEE_PAGE_SIZE = 1000;

/**
 * All attendees for the event (one row per ticket), for the manual-approval name/email look-up. Reuses the open
 * (no role gate) responses feed with `all_attendees=true`; the rows already carry `ticket_id`, so no second
 * per-user fetch is needed to feed the manual validate. Client filters these into the two comboboxes.
 */
export const getEventAttendees = (eventId: string): Promise<ResponsesData[]> =>
	getData(`events/${eventId}/custom-fields/responses?page=1&per_page=${ATTENDEE_PAGE_SIZE}&all_attendees=true`);

/**
 * Gates for the event, used only to attribute the logged-in scanner to a gate name. Cloned (not imported from
 * the green Gates feature) so the Scanner stays self-contained for the downstroke lift.
 */
export const getEventGates = (eventId: string): Promise<EventGate[]> => getData(`events/${eventId}/gates`);

/**
 * Partner members (the assignable-scanner roster) — used by the gate banner to bridge the authenticated user to
 * their `scanner_id` (a member `user_id`), since gates store the member id, not the auth id. Cloned from the
 * green Gates feature so the Scanner stays self-contained. Best-effort: a scanner-role token may lack access, in
 * which case the banner falls back to matching the auth id directly.
 */
export const getPartnerMembers = (): Promise<PartnerSettings> => getData('/partners/settings');
